import { useAppTitle } from '@flobi/asset-library/app-shell';
import { TipsPage, queueTip } from '@flobi/asset-library/hints';
import { TourPath, TourPicture, type OnboardingStep } from '@flobi/asset-library/tours';
import '@flobi/asset-library/tours.css';
import '@flobi/asset-library/share.css';
import '@flobi/asset-library/hints.css';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ARTWORK_HINTS } from '../lib/hints/artworkHints';
import { START_HERE, TIP_COLLECTIONS, TIPS_CONTENT } from '../lib/hints/tipsContent';
import { useTheme } from '../theme';
import { IconButton } from '../ui/controls';
import { usePhone } from '../ui/usePhone';
import { loadSteps, useOnboarding } from '../lib/tours/tourData';
import { PATH_SECTIONS, tourForStep } from '../lib/tours/tours';
import { pictureForStep } from '../lib/tours/pictureScenes';
import { createFile, listFiles } from './api';

/** Where "Learn more" goes: the Flobi Learn site. Unset, the kit leaves the link out. */
const LEARN_URL = (import.meta.env.VITE_FLOBI_LEARN_URL as string | undefined) || undefined;

/** A path row's picture: its tour's own first card picture (lib/tours/pictureScenes). */
const stepPicture = (step: OnboardingStep | undefined) => {
  const html = pictureForStep(step?.action_key);
  return html ? <TourPicture html={html} /> : null;
};

/**
 * Tips (`/tips`, the lightbulb in Home's header): the kit's Tips page with Artwork's collections
 * (lib/hints/tipsContent.ts), in the app's light or dark as it changes. Every tip is about the
 * editor, so "Try it" opens the most recent file (a new one when there is none) and the tip plays
 * there (ArtworkHintsHost › PlayQueuedTip). Above the collections, Your path: Artwork's six tours as
 * lessons in sections, with points and badges (the kit's TourPath, 30 Oct 2026). On a phone a lesson
 * opens its page: no rings there yet.
 */
export function Tips() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [failure, setFailure] = useState<string | null>(null);
  const phone = usePhone();
  const { steps } = useOnboarding();
  useAppTitle('Tips');
  // Read again on each visit, so a tour finished a moment ago shows ticked here.
  useEffect(() => {
    void loadSteps();
  }, []);

  const tryIt = async (id: string) => {
    setFailure(null);
    queueTip('artwork', id);
    try {
      const [last] = await listFiles({ scope: 'recent', limit: 1 });
      const file = last ?? (await createFile());
      navigate(`/f/${file.id}`, { state: { name: file.name } });
    } catch {
      setFailure('Couldn’t open a file to try it in. Try again in a moment.');
    }
  };

  return (
    <div className="h-dvh overflow-hidden bg-page text-label">
      <div className="ui-scroll h-full overflow-y-auto">
        <main className="mx-auto max-w-[1180px] px-[16px] pb-[96px] sm:px-[40px]">
          {/* Home's 72 px band, its back mark lined up with the page's own 24 px inset. No title: the
              page names itself ("Tips" on a collection's way back), so the band says it only once. */}
          <header className="flex h-[72px] items-center px-[24px]">
            <IconButton icon="back" label="Home" size={30} className="-ml-[6px]" onClick={() => navigate('/')} />
          </header>
          {failure ? (
            <p role="alert" className="mb-[8px] px-[24px] text-[13px] text-label-2">
              {failure}
            </p>
          ) : null}
          <TipsPage
            app="artwork"
            theme="artwork"
            mode={theme === 'light' ? 'light' : 'dark'}
            hints={ARTWORK_HINTS}
            collections={TIP_COLLECTIONS}
            content={TIPS_CONTENT}
            learnBase={LEARN_URL}
            startHere={START_HERE}
            onTry={tryIt}
            tour={
              steps.length ? (
                <TourPath
                  steps={steps}
                  tourForStep={phone ? () => null : tourForStep}
                  onRoute={(path) => navigate(path)}
                  sections={PATH_SECTIONS}
                  art={stepPicture}
                  app="artwork"
                  mode={theme === 'light' ? 'light' : 'dark'}
                  appName="Artwork"
                />
              ) : null
            }
          />
        </main>
      </div>
    </div>
  );
}
